import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { useStateCtx } from "@/context/StateContext";
import { useCart } from "@/context/CartContext";
import { notifApi, achievementsApi, pushPrefsApi } from "@/lib/api";
import BudLogo from "@/components/BudLogo";
import PushToggle from "@/components/PushToggle";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  MapPin, Bell, Flame, LogOut, LayoutDashboard, Shield, Sparkles, ChevronDown, ScanLine, Wallet, Menu, X, ShoppingCart, Trophy,
} from "lucide-react";

export default function NavBar({ tone = "light" }) {
  const { user, logout } = useAuth();
  const { current, options, setStateCode } = useStateCtx();
  const { cart } = useCart();
  const loc = useLocation();
  const nav = useNavigate();
  const [notifs, setNotifs] = useState({ items: [], unread: 0 });
  const [achievements, setAchievements] = useState({ achievements: [], unlocked_count: 0, total: 0 });
  const [pushPrefs, setPushPrefs] = useState({ enabled: false, deal_drops_enabled: true, device_count: 0 });
  const [open, setOpen] = useState(false);

  const dark = tone === "dark";

  useEffect(() => {
    if (!user) return;
    notifApi.list().then(setNotifs).catch(() => {});
    achievementsApi.list().then(setAchievements).catch(() => {});
    pushPrefsApi.get().then(setPushPrefs).catch(() => {});
    const i = setInterval(() => {
      notifApi.list().then(setNotifs).catch(() => {});
      achievementsApi.list().then(setAchievements).catch(() => {});
    }, 25000);
    return () => clearInterval(i);
  }, [user]);

  const toggleDealAlerts = async () => {
    try {
      const next = !pushPrefs.deal_drops_enabled;
      await pushPrefsApi.set({ deal_drops_enabled: next });
      setPushPrefs({ ...pushPrefs, deal_drops_enabled: next });
    } catch {/* ignore */}
  };

  const links = [
    { to: "/", label: "Home" },
    { to: "/discover", label: "Discover" },
    { to: "/deals", label: "Deals" },
    { to: "/concierge", label: "Bud" },
    { to: "/community", label: "Community" },
    { to: "/sessions", label: "Sessions" },
    { to: "/about", label: "About" },
    { to: "/pitch", label: "For Stores" },
  ];

  const navBg = dark
    ? "bg-black/40 backdrop-blur-2xl border-b border-white/10"
    : "bg-[#FAF6E8]/85 backdrop-blur-xl border-b-2 border-[#0B0F0C]";
  const linkBase = dark ? "text-white/80 hover:text-[#15FF73]" : "text-[#0B0F0C]/70 hover:text-[#0B0F0C]";

  return (
    <>
    <nav data-testid="navbar" className={`sticky top-0 z-40 ${navBg}`}>
      <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between gap-4">
        <Link to="/" data-testid="navbar-logo" className="flex items-center gap-2 group">
          <BudLogo size="md" tone={dark ? "dark" : "light"} />
          <span className="font-display text-2xl font-bold leading-none tracking-tight">
            X-Seed
          </span>
        </Link>

        <div className="hidden lg:flex items-center gap-7">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={`text-sm font-semibold transition-colors ${
                loc.pathname === l.to ? (dark ? "text-[#15FF73]" : "text-[#0B0F0C] underline decoration-[#FF1A8C] decoration-[3px] underline-offset-8") : linkBase
              }`}
            >
              {l.label}
            </Link>
          ))}
        </div>

        <div className="flex items-center gap-2">
          {/* XP / Streak chip */}
          {user && (
            <div data-testid="xp-chip" className={`hidden md:flex items-center gap-2 px-3 h-10 rounded-full border-2 border-[#0B0F0C] ${dark ? "bg-[#15FF73]" : "bg-[#FFE74C]"}`}>
              <span className="text-base">{user.level?.icon || "🌱"}</span>
              <span className="font-mono-x text-xs font-bold uppercase tracking-wider text-[#0B0F0C]">
                {user.level?.name || "Sprout"} · {user.xp} XP
              </span>
              {(user.streak_days || 0) > 0 && (
                <span className="flex items-center gap-0.5 text-xs font-mono-x font-bold text-[#FF1A8C] border-l-2 border-[#0B0F0C] pl-2">
                  <Flame size={11} fill="currentColor" /> {user.streak_days}
                </span>
              )}
            </div>
          )}

          {/* State */}
          <DropdownMenu>
            <DropdownMenuTrigger
              data-testid="state-switcher"
              className={`hidden sm:flex items-center gap-1.5 px-3 h-10 rounded-full border-2 ${dark ? "border-white/30 text-white hover:bg-white/10" : "border-[#0B0F0C] hover:bg-[#15FF73]"} transition-colors text-xs font-mono-x font-bold uppercase tracking-wider`}
            >
              <MapPin size={13} strokeWidth={2.4} />
              {current.code}
              <ChevronDown size={12} strokeWidth={2.4} />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-2xl border-2 border-[#0B0F0C] bg-[#FAF6E8] shadow-[6px_6px_0_#0B0F0C] w-64">
              <DropdownMenuLabel className="label-overline text-[#FF1A8C]">Region</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {options.map((o) => (
                <DropdownMenuItem
                  key={o.code}
                  data-testid={`state-option-${o.code}`}
                  onClick={() => setStateCode(o.code)}
                  className="text-sm flex justify-between cursor-pointer focus:bg-[#15FF73] rounded-lg"
                >
                  <span>{o.label}</span>
                  <span className="font-mono-x text-[#FF1A8C] font-bold">{o.code}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Cart */}
          <Link
            to="/cart"
            data-testid="navbar-cart"
            className={`relative flex items-center justify-center w-10 h-10 rounded-full border-2 ${dark ? "border-white/30 text-white hover:bg-white/10" : "border-[#0B0F0C] hover:bg-[#15FF73]"} transition-colors`}
          >
            <ShoppingCart size={15} strokeWidth={2.2} />
            {(cart?.count || 0) > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#15FF73] border-2 border-[#0B0F0C] text-[10px] font-bold text-[#0B0F0C] flex items-center justify-center">
                {cart.count}
              </span>
            )}
          </Link>

          {/* Push toggle (subscribe to deal-drop alerts) */}
          {user && <PushToggle compact />}

          {/* Notifications */}
          {user && (
            <Popover>
              <PopoverTrigger asChild>
                <button
                  data-testid="notif-bell"
                  onClick={() => notifApi.markRead().then(() => setNotifs({ ...notifs, unread: 0 }))}
                  className={`relative flex items-center justify-center w-10 h-10 rounded-full border-2 ${dark ? "border-white/30 text-white hover:bg-white/10" : "border-[#0B0F0C] hover:bg-[#FFE74C]"} transition-colors`}
                >
                  <Bell size={15} strokeWidth={2.2} />
                  {notifs.unread > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#FF1A8C] border-2 border-[#0B0F0C] text-[10px] font-bold text-white flex items-center justify-center">
                      {notifs.unread}
                    </span>
                  )}
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="rounded-2xl border-2 border-[#0B0F0C] bg-[#FAF6E8] shadow-[6px_6px_0_#0B0F0C] w-80 p-0">
                <div className="p-4 border-b-2 border-[#0B0F0C] bg-[#15FF73] rounded-t-2xl">
                  <div className="font-display text-xl font-bold">Drops & Wins</div>
                </div>
                {achievements.total > 0 && (
                  <div className="p-3 border-b-2 border-[#0B0F0C] bg-[#FFE74C]/40" data-testid="navbar-achievements">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-1.5 text-xs font-mono-x font-bold uppercase tracking-widest"><Trophy size={11} strokeWidth={2.4} /> Achievements</div>
                      <div className="text-xs font-mono-x font-bold">{achievements.unlocked_count} / {achievements.total}</div>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {achievements.achievements.map((a) => (
                        <span
                          key={a.id}
                          title={`${a.label} — ${a.blurb}${a.unlocked ? "" : " (locked)"}`}
                          className={`text-base w-7 h-7 flex items-center justify-center rounded-full border-2 border-[#0B0F0C] ${a.unlocked ? "bg-[#15FF73]" : "bg-[#0B0F0C]/10 grayscale opacity-40"}`}
                          data-testid={`achievement-${a.id}`}
                        >{a.icon}</span>
                      ))}
                    </div>
                  </div>
                )}
                {/* Deal-drop alerts toggle */}
                <div className="p-3 border-b-2 border-[#0B0F0C] bg-[#FAF6E8] flex items-center justify-between">
                  <div>
                    <div className="text-xs font-mono-x font-bold uppercase tracking-widest">Deal-drop alerts</div>
                    <div className="text-[10px] text-[#0B0F0C]/55">Ping when nearby shops drop new deals</div>
                  </div>
                  <button
                    data-testid="deal-alerts-toggle"
                    onClick={toggleDealAlerts}
                    className={`relative w-10 h-6 rounded-full border-2 border-[#0B0F0C] transition-colors ${pushPrefs.deal_drops_enabled ? "bg-[#15FF73]" : "bg-[#0B0F0C]/10"}`}
                  >
                    <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white border-2 border-[#0B0F0C] transition-all ${pushPrefs.deal_drops_enabled ? "left-[18px]" : "left-0.5"}`} />
                  </button>
                </div>
                <div className="max-h-[280px] overflow-auto">
                  {notifs.items.length === 0 && (
                    <div className="p-8 text-center text-sm text-[#0B0F0C]/50">No alerts yet — go claim a deal 🌿</div>
                  )}
                  {notifs.items.map((n) => (
                    <div key={n.id} className="p-3 border-b border-[#0B0F0C]/10 hover:bg-[#FFE74C]/30">
                      <div className="font-bold text-sm">{n.title}</div>
                      <div className="text-xs text-[#0B0F0C]/70">{n.body}</div>
                    </div>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          )}

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger
                data-testid="user-menu-trigger"
                className={`flex items-center gap-2 px-2 h-10 rounded-full border-2 ${dark ? "border-white/30 hover:bg-white/10 text-white" : "border-[#0B0F0C] hover:bg-[#15FF73]"} text-sm font-bold`}
              >
                <div className="w-7 h-7 rounded-full bg-[#FF1A8C] flex items-center justify-center text-white text-xs font-bold border-2 border-[#0B0F0C]">
                  {user.name?.[0]?.toUpperCase()}
                </div>
                <span className="hidden sm:inline pr-2">{user.name?.split(" ")[0]}</span>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-2xl border-2 border-[#0B0F0C] bg-[#FAF6E8] shadow-[6px_6px_0_#0B0F0C] w-60">
                <DropdownMenuLabel>
                  <div className="text-xs text-[#0B0F0C]/60">{user.email}</div>
                  <div className="font-mono-x text-[#FF1A8C] text-[10px] uppercase mt-1 font-bold">{user.role}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => nav("/me")} className="cursor-pointer focus:bg-[#15FF73] rounded-lg">
                  <Sparkles size={14} className="mr-2" /> My XP & Claims
                </DropdownMenuItem>
                {(user.role === "dispensary" || user.role === "admin") && (
                  <>
                    <DropdownMenuItem data-testid="menu-dashboard" onClick={() => nav("/dashboard")} className="cursor-pointer focus:bg-[#15FF73] rounded-lg">
                      <LayoutDashboard size={14} className="mr-2" /> Dashboard
                    </DropdownMenuItem>
                    <DropdownMenuItem data-testid="menu-scan" onClick={() => nav("/scan")} className="cursor-pointer focus:bg-[#15FF73] rounded-lg">
                      <ScanLine size={14} className="mr-2" /> Scanner
                    </DropdownMenuItem>
                    <DropdownMenuItem data-testid="menu-wallet" onClick={() => nav("/wallet")} className="cursor-pointer focus:bg-[#15FF73] rounded-lg">
                      <Wallet size={14} className="mr-2" /> Wallet
                    </DropdownMenuItem>
                    <DropdownMenuItem data-testid="menu-billing" onClick={() => nav("/billing")} className="cursor-pointer focus:bg-[#15FF73] rounded-lg">
                      <Sparkles size={14} className="mr-2" /> Billing
                    </DropdownMenuItem>
                  </>
                )}
                {user.role === "admin" && (
                  <DropdownMenuItem data-testid="menu-admin" onClick={() => nav("/admin")} className="cursor-pointer focus:bg-[#15FF73] rounded-lg">
                    <Shield size={14} className="mr-2" /> Admin
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-privacy" onClick={() => nav("/privacy")} className="cursor-pointer focus:bg-[#15FF73] rounded-lg text-xs">
                  <Shield size={12} className="mr-2" /> Privacy
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-logout" onClick={() => { logout(); nav("/"); }} className="cursor-pointer focus:bg-[#FF1A8C] focus:text-white rounded-lg">
                  <LogOut size={14} className="mr-2" /> Sign out
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="menu-delete-account"
                  onClick={async () => {
                    if (!window.confirm("Soft-delete your account? PII purged now, hard delete after 30 days. This cannot be undone.")) return;
                    try {
                      const { auth } = await import("@/lib/api");
                      await auth.deleteMe();
                    } catch (e) { /* still log out */ }
                    logout();
                    nav("/");
                  }}
                  className="cursor-pointer focus:bg-[#FF1A8C] focus:text-white rounded-lg text-xs"
                >
                  <LogOut size={12} className="mr-2" /> Delete account
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Link to="/auth?mode=login" data-testid="nav-login" className={`hidden sm:flex items-center text-sm font-bold ${linkBase} px-3`}>Sign in</Link>
              <Link to="/auth?mode=register" data-testid="nav-register" className="btn-x btn-primary !py-2 !px-4 !text-sm">Join free →</Link>
            </>
          )}

          <button onClick={() => setOpen(!open)} className={`lg:hidden flex items-center justify-center w-10 h-10 rounded-full border-2 ${dark ? "border-white/30 text-white" : "border-[#0B0F0C]"}`}>
            {open ? <X size={16} /> : <Menu size={16} />}
          </button>
        </div>
      </div>
      {open && (
        <div className="lg:hidden border-t-2 border-[#0B0F0C] bg-[#FAF6E8]">
          <div className="px-5 py-4 flex flex-col gap-3">
            {links.map((l) => (
              <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="font-bold text-lg">{l.label}</Link>
            ))}
          </div>
        </div>
      )}
    </nav>
    </>
  );
}
